import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero, LightSection } from '@/components/PageShell';
import { FundTheGapCTA } from '@/components/content/FundTheGapCTA';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';
import { GRANT_CATEGORIES, GRANTS_LAST_VERIFIED, grants, type GrantCategory } from '@/lib/data/grants';
import { cn } from '@/lib/utils';

const TITLE = 'Youth Sports Grants & Financial Aid Directory (2026)';
const DESCRIPTION =
  'A verified directory of grants, fee assistance and equipment programs for youth and amateur athletes in the US — who qualifies, what they cover and how to apply.';

const APPLY_LABEL = {
  individual: 'Families apply',
  organization: 'Team / club applies',
  both: 'Families or teams apply',
} as const;

const FAQS = [
  {
    q: 'Are there grants for individual youth athletes, not just teams?',
    a: 'Yes, but fewer than for teams. Need-based fee-assistance funds and several sport-specific foundations accept applications from families directly; most equipment programs only donate to a team, school or nonprofit program, so ask your coach or club to apply.',
  },
  {
    q: 'How much do youth sports grants usually cover?',
    a: 'Most need-based youth grants cover part of a season — registration, a piece of equipment, or one trip — rather than the full cost. Amounts are set by each organization and many vary year to year, so check the program page before you plan around a number.',
  },
  {
    q: 'What should I prepare before applying?',
    a: "A short description of the athlete and the sport, an itemized season budget (fees, travel, gear), proof of household income for need-based programs, and a coach's reference. The same budget works for a fund page, so the effort isn't wasted if a grant says no.",
  },
  {
    q: 'What if a grant does not cover the whole season?',
    a: 'Stack sources: a partial grant, a payment plan with the club, and a fund page where family and friends give monthly. Ten supporters at $20 a month is $2,400 over a year.',
  },
];

export default function YouthSportsGrants() {
  usePageMeta(TITLE, DESCRIPTION, {
    article: true,
    image: '/resources/coach-and-athlete.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Youth Sports Grants' }],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: TITLE,
        numberOfItems: grants.length,
        itemListElement: grants.map((g, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: g.name,
          url: g.url,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });

  const [category, setCategory] = useState<GrantCategory | 'all'>('all');
  const [individualOnly, setIndividualOnly] = useState(false);

  const visible = useMemo(
    () =>
      grants.filter(
        (g) =>
          (category === 'all' || g.category === category) &&
          (!individualOnly || g.applyAs !== 'organization'),
      ),
    [category, individualOnly],
  );

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Find money · Directory"
          title={
            <>
              Youth sports grants{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                that actually exist.
              </span>
            </>
          }
          lead={`${grants.length} verified programs that help families pay for fees, travel, training and gear — what each covers, who qualifies, and whether you or your club applies.`}
          size="sm"
        />
      }
    >
      <LightSection>
        <div className="max-w-4xl">
          <p className="text-slate-600 leading-relaxed">
            Grants are worth an afternoon of applications, but they rarely close a whole season on their own: most
            cover one piece (a registration, a trip, a set of gear) and many go to teams rather than individual
            athletes. Use this list to find what you qualify for, then plan the rest with a{' '}
            <Link to="/resources/how-to-set-a-fundraising-goal" className="text-blue-700 underline">
              season budget
            </Link>
            .
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Last checked {GRANTS_LAST_VERIFIED}. Amounts and windows change — always confirm on the program's own page.
            Sport Fund is not affiliated with any program listed.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-2" role="group" aria-label="Filter by category">
          {[{ id: 'all' as const, label: 'All' }, ...GRANT_CATEGORIES].map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              aria-pressed={category === c.id}
              className={cn(
                'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                category === c.id
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
              )}
            >
              {c.label}
            </button>
          ))}
          <label className="ml-1 inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={individualOnly}
              onChange={(e) => setIndividualOnly(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Families can apply directly
          </label>
        </div>

        {GRANT_CATEGORIES.filter((c) => category === 'all' || c.id === category).map((c) => {
          const items = visible.filter((g) => g.category === c.id);
          if (items.length === 0) return null;
          return (
            <section key={c.id} className="mt-12" aria-labelledby={`cat-${c.id}`}>
              <h2 id={`cat-${c.id}`} className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
                {c.label}
              </h2>
              <p className="mt-1 text-slate-500">{c.blurb}</p>
              <div className="mt-6 grid md:grid-cols-2 gap-5">
                {items.map((g) => (
                  <article key={g.name} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-lg font-bold text-slate-900 leading-snug">
                        <a href={g.url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-700">
                          {g.name}
                        </a>
                      </h3>
                      <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                        {APPLY_LABEL[g.applyAs]}
                      </span>
                    </div>
                    <dl className="mt-4 space-y-2 text-sm">
                      <div>
                        <dt className="inline font-semibold text-slate-900">For: </dt>
                        <dd className="inline text-slate-600">{g.forWho}</dd>
                      </div>
                      <div>
                        <dt className="inline font-semibold text-slate-900">Covers: </dt>
                        <dd className="inline text-slate-600">{g.covers}</dd>
                      </div>
                      <div>
                        <dt className="inline font-semibold text-slate-900">Amount: </dt>
                        <dd className="inline text-slate-600">{g.amount}</dd>
                      </div>
                      <div>
                        <dt className="inline font-semibold text-slate-900">How to apply: </dt>
                        <dd className="inline text-slate-600">{g.howToApply}</dd>
                      </div>
                    </dl>
                    <a
                      href={g.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-700"
                    >
                      Visit program <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </article>
                ))}
              </div>
            </section>
          );
        })}
        {visible.length === 0 && <p className="mt-12 text-slate-500">No programs match that filter.</p>}

        <div className="max-w-3xl">
          <FundTheGapCTA />

          <h2 className="mt-12 text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-6">
            {FAQS.map((f) => (
              <div key={f.q}>
                <h3 className="font-semibold text-lg text-slate-900 mb-1">{f.q}</h3>
                <p className="text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>

          <h2 className="mt-12 text-2xl font-extrabold tracking-tight text-slate-900">Keep planning</h2>
          <ul className="mt-4 space-y-2 text-blue-700">
            <li>
              <Link to="/resources/fundraising-guides">Fundraising guides by sport</Link>
            </li>
            <li>
              <Link to="/resources/national-championships-calendar">Road to Nationals: championship calendar & trip budget</Link>
            </li>
            <li>
              <Link to="/resources/cost-of-youth-sports-2026">What a season of youth sports costs in 2026</Link>
            </li>
          </ul>
          <ArticleFeedback articleSlug="youth-sports-grants" />
        </div>
      </LightSection>
    </PageShell>
  );
}
